<script lang="ts" setup>
import { __ } from '@@/bits/intl'
import ImageThumbnail from '@@/library/v4/components/ImageThumbnail.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzIconButton, {
  OzIconButtonColorScheme,
  OzIconButtonHrefMode,
  OzIconButtonSizePreset,
} from '@@/library/v4/components/OzIconButton.vue'
import OzListRow, { OzListRowSeparator } from '@@/library/v4/components/OzListRow.vue'
import OzRelativeTime from '@@/library/v4/components/OzRelativeTime.vue'
import OzTooltip from '@@/library/v4/components/OzTooltip.vue'
import type { Wall } from '@@/types'
import { type ComponentPublicInstance, computed, shallowRef } from 'vue'

const { separator = OzListRowSeparator.Short, wall } = defineProps<{
  wall: Wall
  separator: OzListRowSeparator
}>()

const organizationLabel = computed((): string => {
  return wall.is_organization_restricted === true ? __('Organization Only') : __('Private')
})

const organizationMoreInfo = computed((): string => {
  return wall.is_organization_restricted === true
    ? __('Only people in your organization can access')
    : __('Only the owner and collaborators can access')
})

const emit = defineEmits<{
  (name: 'select', wall: Wall): void
}>()

const title = computed((): string => {
  return wall.title || __('Untitled padlet')
})

function select(): void {
  /**
   * Select the wall.
   */
  emit('select', wall)
}

const changePrivacySettingsButton = shallowRef<ComponentPublicInstance | null>(null)
const changePrivacySettingsButtonTooltipAnchor = computed(
  () => (changePrivacySettingsButton.value?.$el ?? null) as HTMLElement | null,
)
const xChangePrivacySettingsButtonTooltip = shallowRef(false)

// The share panel is where we change privacy settings
const wallShowSharePanelUrl = computed((): string => {
  return (wall as Wall & { show_url: string }).show_url + '#share'
})
</script>

<template>
  <OzListRow
    :separator="separator"
    class="hhover:bg-light-ui-300 dark:hhover:bg-dark-ui-300 focus-visible:ring-2 ring-grape-500 dark:ring-canary-500 px-2"
    :data-testid="`libraryLtiWallListRow${wall.title}`"
    @click="select"
  >
    <template #left>
      <ImageThumbnail
        :class="['shrink-0', 'rounded-lg rounded-ee-2xl', 'me-6']"
        :src="wall.thumbnail.url"
        :title="wall.title"
        :placeholder-color-value="wall.thumbnail.dominant_color_as_rgb"
        :placeholder-color="'random'"
        :fallback-to-placeholder="true"
        :original-image-width="wall.thumbnail.width || 40"
        :original-image-height="wall.thumbnail.height || 40"
        :width="40"
        :process-image="true"
        :aspect-ratio="1"
      />
    </template>

    <div class="my-2 relative overflow-hidden">
      <div
        :title="title"
        class="text-17-22 text-dark-text-100 dark:text-light-text-100 line-clamp-2 break-word-anywhere mb-0.5"
      >
        {{ title }}
      </div>
      <div class="text-14-16 text-dark-text-200 dark:text-light-text-200 flex">
        <span class="truncate">{{ wall.builder.display_name }}</span>
        <span class="text-dark-text-300 dark:text-light-text-300"> &nbsp;•&nbsp; </span>
        <OzRelativeTime :datetime="wall.updated_at" dateformat="long" />
      </div>
    </div>

    <template v-if="wall.is_private || wall.is_organization_restricted" #right>
      <div class="flex items-center shrink-0 tablet-portrait:pe-2">
        <OzIcon name="password_outline" :size="16" class="text-dark-text-200 dark:text-light-text-200" />

        <div class="flex flex-col ms-2">
          <span
            class="text-14-16 text-dark-text-100 dark:text-light-text-100"
            data-testid="libraryLtiWallListRowPrivacyLabel"
          >
            {{ organizationLabel }}
            <!-- OzIconButton has display: flex to to inline it we have a wrapper <span> -->
            <span class="inline-block">
              <!-- We show our own tooltip -> set title to an empty string -->
              <OzIconButton
                ref="changePrivacySettingsButton"
                data-testid="libraryLtiWallListRowChangePrivacySettingsButton"
                :label="__('Change privacy settings')"
                title=""
                :icon-name="'external_link'"
                :size-preset="OzIconButtonSizePreset.H24px"
                :color-scheme="OzIconButtonColorScheme.SecondaryClear"
                :href="wallShowSharePanelUrl"
                :href-mode="OzIconButtonHrefMode.NewTab"
                @focus="xChangePrivacySettingsButtonTooltip = true"
                @blur="xChangePrivacySettingsButtonTooltip = false"
                @mouseover="xChangePrivacySettingsButtonTooltip = true"
                @mouseout="xChangePrivacySettingsButtonTooltip = false"
                @click.stop
              />
            </span>
          </span>
          <span class="hidden phone:inline-block text-12-16 text-dark-text-200 dark:text-light-text-200">{{
            organizationMoreInfo
          }}</span>
        </div>
        <OzTooltip
          v-if="xChangePrivacySettingsButtonTooltip && changePrivacySettingsButtonTooltipAnchor"
          :anchor="changePrivacySettingsButtonTooltipAnchor"
          :config="{ placement: 'bottom-start' }"
        >
          {{ __('Change privacy settings') }}
        </OzTooltip>
      </div>
    </template>
  </OzListRow>
</template>
